import { useState, useEffect, useRef } from "react";
import axiosVortex from "../../../utils/axios.instance";
import {
  OrderErrorResponse,
  OrderPayload,
  OrderSuccessResponse,
  UseOrderRequestResult,
} from "../../../models/orders";

const useOrderRequest = (payload: OrderPayload): UseOrderRequestResult => {
  const [data, setData] = useState<OrderSuccessResponse | OrderErrorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Use AbortController to handle cancellation of in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Track if the component is mounted
  const isMounted = useRef(true);

  // Cleanup function
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // Validate payload before making the request
    if (!payload || !payload.token || !payload.exchange) {
      setError("Invalid payload parameters");
      return;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    const placeOrder = async () => {
      setLoading(true);
      setError(null);
      
      // Don't immediately clear data to prevent UI flicker on repeated requests
      // for the same data
      
      try {
        // Ensure price is a valid number
        const validatedPayload = {
          ...payload,
          price: isNaN(payload.price) || payload.price <= 0 ? 1 : payload.price,
          quantity: payload.quantity || 1 // Ensure quantity is at least 1
        };
        
        const response = await axiosVortex.post(`margins/order`, validatedPayload, {
          signal: abortControllerRef.current?.signal,
          timeout: 10000 // 10 second timeout
        });

        if (!isMounted.current) return;

        if (response.data.status === "success") {
          const successData: OrderSuccessResponse = {
            available_margin: parseFloat(response.data.available_margin) || 0,
            required_margin: parseFloat(response.data.required_margin) || 1, // Prevent division by zero
            status: "success",
          };
          setData(successData);
        } else {
          const errorData: OrderErrorResponse = {
            message: response.data.message || "An unknown error occurred",
            status: "error",
          };
          setData(errorData);
        }
      } catch (err: any) {
        if (!isMounted.current) return;
        
        // Don't set error state if the request was aborted
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }
        
        const errorMessage =
          err?.response?.data?.message || err.message || "An unknown error occurred";
        const errorData: OrderErrorResponse = {
          message: errorMessage,
          status: "error",
          response: err.response || null,
        };
        setData(errorData);
        setError(errorMessage);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    // Debounce the API call to prevent excessive requests
    const timeoutId = setTimeout(() => {
      placeOrder();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    payload?.token,
    payload?.exchange,
    payload?.transaction_type,
    payload?.product,
    payload?.variety,
    payload?.quantity,
    payload?.price,
    payload?.mode,
    payload?.old_quantity,
    payload?.old_price
  ]);

  return { data, loading, error };
};

export default useOrderRequest;